import React, { ForwardedRef, forwardRef, memo, useCallback } from 'react'
import ReactMarkdown from 'react-markdown'
import { CardHeader, CardContent, Typography, Grid, Chip, Card, CardProps, CardActions, Divider } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import Tags from 'components/Tags'
import Tag from 'components/Tag'
import JobShare from 'components/JobShare'
import Condition from 'components/Condition'
import StyledLink from 'components/StyledLink'
import Button from 'components/Button'
import MoreButtonDialog from 'components/MoreButtonDialog'
import ButtonStack from 'components/ButtonStack'
import { getRateText } from 'services/job'
import RateExplanation from 'components/JobPosting/components/RateExplanation'
import { buildQueryParams } from 'services/router'
import { useFragment } from 'react-relay/hooks'
import { graphql } from 'react-relay'
import { JobsListingItem_Job$key } from '__generated__/JobsListingItem_Job.graphql'
import JobSkillsCollection from 'components/JobSkillsCollection'
import { useIsUnsentJobApplication } from 'hooks/jobApplication'
import { useMediaQuery } from 'hooks/useMediaQuery'
import { Check, Share } from '@mui/icons-material'
import CloseIcon from '@mui/icons-material/Close'
import ModeIcon from '@mui/icons-material/Mode'
import { Nullable } from 'types'
import { JobsListingItem_JobApplication$key } from '__generated__/JobsListingItem_JobApplication.graphql'
import ButtonStackSpacer from 'components/ButtonStackSpacer'
import useIsIframe from 'hooks/useIsIframe'
import ApplicationStepsButton from './components/ApplicationStepsButton'
import ApplicationStatusTextTag from './components/ApplicationStatusTextTag'

interface IJobProps extends CardProps {
  jobFragmentRef: JobsListingItem_Job$key
  jobApplicationFragmentRef?: Nullable<JobsListingItem_JobApplication$key>
  disableCompanyName?: boolean
  disableApplicationSteps?: boolean
}

const JobsListingItem = (props: IJobProps, ref: ForwardedRef<HTMLDivElement>) => {
  const { jobFragmentRef, jobApplicationFragmentRef, disableCompanyName, disableApplicationSteps, ...restProps } = props

  const job = useFragment(graphql`
    fragment JobsListingItem_Job on Job {
      title
      slug
      description
      positionTypes
      rateMode
      clientRate {
        value
      }
      minClientRate {
        value
      }
      freelancerRate {
        value
      }
      minFreelancerRate {
        value
      }
      currency {
        code
      }
      firm {
        slug
        name
      }
      jobSkills {
        required
        requiredYears
        groupIndex
        skill {
          id
          name
        }
        userSkill {
          experience
        }
      }
      jobApplication {
        ...JobsListingItem_JobApplication
      }

      ...RateExplanation_Job
      ...JobShare_Job
    }
  `, jobFragmentRef)

  const jobApplication = useFragment(graphql`
    fragment JobsListingItem_JobApplication on JobApplication {
      id
      status

      ...ApplicationStepsButton_JobApplication
      ...ApplicationStatusTextTag_JobApplication

      ...jobApplication_useIsUnsentJobApplication
    }
`, (jobApplicationFragmentRef || job?.jobApplication) as JobsListingItem_JobApplication$key)

  const theme = useTheme()
  const isIframe = useIsIframe()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))

  const handleRateExplanationClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    // Prevent click from triggering card click
    e.stopPropagation()
    e.preventDefault()
  }, [])

  let chipStatus
  let chipIcon

  switch (jobApplication?.status) {
    case 'job_application_draft':
      chipStatus = 'Draft'
      chipIcon = <ModeIcon />
      break

    case 'job_application_sent':
      chipStatus = 'Sent'
      chipIcon = <Check />
      break

    case 'rejected':
      chipStatus = 'Rejected'
      chipIcon = <CloseIcon />
      break
  }

  const isUnsentJobApplication = useIsUnsentJobApplication(jobApplication)

  if (!job) return null

  let query = isIframe ? `${buildQueryParams({ ref: 'iframe' })}` : ''
  let jobDescription = job.description.length > 300 ? `${job.description.substring(0, 299)}...` : job.description

  // fixes missing space around unwrapped paragraphs
  jobDescription = jobDescription?.replace(/\n/g, ' ')?.replace(/\r/g, ' ')

  const applied = Boolean(job.jobApplication) && !isUnsentJobApplication
  const isPermanent = job.positionTypes?.includes('permanent')
  const isFreelance = job.positionTypes?.includes('freelancer')
  const minRate = job.minClientRate?.value || job.minFreelancerRate?.value
  const maxRate = job.clientRate?.value || job.freelancerRate?.value

  let jobType: string

  if (isPermanent && isFreelance) {
    jobType = 'Permanent and Freelance'
  } else if (isPermanent) {
    jobType = 'Permanent'
  } else if (isFreelance) {
    jobType = 'Freelance'
  }

  const permRateText = getRateText(
    minRate,
    maxRate,
    job.rateMode,
    'year',
    job.currency?.code,
  )?.replace('/year', '/annual')

  const freelanceRateText = getRateText(
    minRate,
    maxRate,
    job.rateMode,
    'day',
    job.currency?.code,
  )

  const jobSkills = job?.jobSkills?.map(js => ({
    id: js.skill?.id,
    name: js.skill?.name,
    experience: js.userSkill?.experience,
    required: js.required,
    required_years: js.requiredYears,
    group_index: js.groupIndex,
  }))

  return (
    <Card
      data-cy="job-item"
      data-cy-job-slug={job.slug}
      {...restProps}
      ref={ref}
    >
      <CardHeader
        title={(
          <React.Fragment>
            <Typography variant="h4">
              <Tags dense>
                <Tag>
                  <StyledLink href={`/${job.firm?.slug}/${job.slug}${query}`} style={{ color: theme?.palette?.secondary?.main }}>
                    {job.title}
                  </StyledLink>
                </Tag>

                <Condition condition={!disableCompanyName}>
                  <Tag>
                    <StyledLink href={`/${job.firm.slug}`} style={{ color: theme?.palette?.secondary?.main }}>
                      {job.firm.name}
                    </StyledLink>
                  </Tag>
                </Condition>
              </Tags>
            </Typography>
          </React.Fragment>
        )}
        subheader={(
          <React.Fragment>
            {chipStatus && (
              <Chip
                size="small"
                color="primary"
                icon={chipIcon}
                sx={{
                  color: 'white',
                  marginTop: theme.spacing(1),
                  marginBottom: theme.spacing(1),
                  marginRight: theme.spacing(1),
                }}
                label={chipStatus}
              />
            )}

            <Tags dense>
              <ApplicationStatusTextTag jobApplicationFragmentRef={jobApplication} />

              <Tag>
                {jobType}
              </Tag>

              {isPermanent && permRateText && (
                <Tag>
                  {permRateText}
                </Tag>
              )}

              {isFreelance && freelanceRateText && (
                <Tag>
                  {freelanceRateText}

                  <div
                    style={{ display: 'inline-block', margin: theme.spacing(-2, 0) }}
                    role="button"
                    onClick={handleRateExplanationClick}
                  >
                    <RateExplanation
                      job={job}
                    />
                  </div>
                </Tag>
              )}
            </Tags>
          </React.Fragment>
        )}
        action={(
          <React.Fragment>
            <Condition condition={isSmallScreen}>
              <MoreButtonDialog dialogTitle="Share this job" icon={<Share />}>
                <JobShare jobFragmentRef={job} />
              </MoreButtonDialog>
            </Condition>

            <Condition condition={!isSmallScreen}>
              <JobShare jobFragmentRef={job} />
            </Condition>
          </React.Fragment>
        )}
      />
      <Divider />
      <CardContent>
        <div>
          <Grid container spacing={2}>
            {jobSkills && jobSkills.length > 0 && (
              // TODO: fix id vs skill_id mismatch
              (
              <Grid item xs={12}>
                <JobSkillsCollection skills={jobSkills?.map(skill => ({ ...skill, skill_id: skill.id }))} />
              </Grid>
              )
            )}

            {job.description && (
              <Grid item xs={12}>
                <Typography variant="body1">
                  {/*
                    TODO: this will cause an SSR crash if the job description contains any links
                    because the whole job card is a link and <a> tags can't contain other <a> tags.
                    This should be fixed by writing our own ReactMarkdown wrapper that changes how the md is rendered
                    Or by not making the whole card a link anymore, or similar solutions
                    Until then, hydration will crash when the job description has links
                  */}

                  <ReactMarkdown
                    source={jobDescription}
                    allowedTypes={['root', 'text', 'strong', 'emphasis', 'delete', 'link', 'inlineCode']}
                    unwrapDisallowed
                  />
                </Typography>
              </Grid>
            )}
          </Grid>
        </div>
      </CardContent>
      <Divider />
      <CardActions>
        <ButtonStack fullWidth>
          <ButtonStackSpacer />

          <Condition condition={!disableApplicationSteps}>
            <ApplicationStepsButton jobApplicationFragmentRef={jobApplication} />
          </Condition>

          <Button
            color="primary"
            component={StyledLink}
            href={`/${job.firm?.slug || 'job'}/${job.slug}${query}`}
            data-cy="apply"
          >
            <Condition condition={applied}>
              View Job
            </Condition>

            <Condition condition={!applied}>
              View &amp; Apply
            </Condition>
          </Button>
        </ButtonStack>
      </CardActions>
    </Card>
  )
}

export default memo(forwardRef(JobsListingItem))
